import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { displayValue } from "@/lib/helper";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import MealPlanForm from "../modal/form";
import { useGetMealPlansQuery } from "@/services/mealPlansApi";
import TableFilters from "@/components/ui/table/data-table-filter";
import { useGetFoodsQuery } from "@/services/foodsApi";
import { useGetMembersListQuery } from "@/services/memberApi";
import { visibleFor } from "@/lib/constants/meal_plans";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { MealPlanDataType } from "@/types/meal-plan";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";

const visibleForMap = Object.fromEntries(
  visibleFor.map((vf) => [vf.label, vf.value])
);

const { VITE_VIEW_S3_URL } = import.meta.env;

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  total_nutrition?: number;
  fat?: number;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "meal_plan",
};

export default function MealPlansTableView() {
  const meal_plan = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).meal_plan ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [data, setData] = useState<MealPlanDataType | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("open") === "true") {
      handleOpen();
      
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("open");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // search input
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const {
    data: mealsData,
    isLoading,
    refetch,
  } = useGetMealPlansQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );

  const { data: foodData } = useGetFoodsQuery(
    { org_id: orgId, query: `sort_order=desc&sort_key=created_at` },
    {
      skip: query == "",
    }
  );

  const { data: membersData } = useGetMembersListQuery({
    id: Number(orgId),
    query: "",
  });

  const mealstableData = React.useMemo(() => {
    return Array.isArray(mealsData?.data) ? mealsData?.data : [];
  }, [mealsData]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const actionsColumn: ColumnDef<MealPlanDataType> = {
    accessorKey: "actions",
    header: ({ table }) => <span>Action</span>,
    cell: ({ row }) => {
      return (
        <DataTableRowActions
          access={meal_plan}
          handleEdit={handleEdit}
          data={row.original}
          refetch={refetch}
        />
      );
    },
    enableSorting: false,
    enableHiding: false,
  };

  const columns: ColumnDef<MealPlanDataType>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="size-8 flex gap-2 items-center justify-between w-fit">
            {row.original.profile_img ? (
                <img
                  src={
                    row.original.profile_img.includes('https://')
                      ? row.original.profile_img
                      : `${VITE_VIEW_S3_URL}/${row.original.profile_img}`
                  }
                  alt={row.original.meal_plan_name}
                  loading="lazy"
                  className="size-8 bg-gray-100 object-cover rounded-sm "
                />
              
            ) : (
              <div className="size-8 bg-gray-100 rounded-sm"></div>
            )}
            <div className="relative">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p
                      className="capitalize cursor-pointer truncate w-[100px]" // Set a fixed width and truncate text
                      style={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {displayValue(row.original.meal_plan_name)}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="capitalize text-sm">
                      {displayValue(row.original.meal_plan_name)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "visible_for",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Visible For"
          sortKey="visible_for"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.visible_for}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "carbs",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Carbs"
          sortKey="carbs"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.carbs}%</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "protein",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Protein"
          sortKey="protein"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.protein}%</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "fats",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Fats"
          sortKey="fats"
          // sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.fats}%</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    ...(meal_plan !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: mealstableData as MealPlanDataType[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const handleOpen = () => {
    setAction("add");
    setData(undefined);
    setIsDialogOpen(true);
  };

  const handleEdit = (data: MealPlanDataType) => {
    console.log({ data }, "edit modal");
    const payload = {
      ...data,
      visible_for: visibleForMap[data.visible_for!],
    };
    setAction("edit");
    setData(payload);
    setIsDialogOpen(true);
  };

  function handleFilterChange(field: string, value: string | number) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = [
    {
      type: "select",
      name: "visible_for",
      label: "Visible For",
      options: visibleFor.map((item) => ({ id: item.value, name: item.label })),
      function: (value: string) => handleFilterChange("visible_for", value),
    },
    {
      type: "multiselect",
      name: "food_id",
      label: "Food",
      options: foodData?.data?.map((food) => ({
        value: food.id,
        label: food.name,
      })),
      function: (value: string) => handleFilterChange("food_id", value),
    },
    {
      type: "multiselect",
      name: "member_id",
      label: "Members",
      options: membersData,
      function: (value: string) => handleFilterChange("member_id", value),
    },
  ];

 const totalRecords = mealsData?.filtered_counts ?? 0;

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });


  return (
    <div className="w-full space-y-4">
      <div className="flex flex-row items-center justify-between gap-2 px-3">
        {/* Table Filter component */}
        <div className="flex-initial sm:flex-1 min-w-0">
          <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />
        </div>

        {/* Buttons Container */}
        <div className="flex flex-row items-center gap-1 sm:gap-2 shrink-0">
          {meal_plan !== "read" && (
            <Button
              className="bg-primary text-sm text-black flex items-center justify-center h-8 px-2 min-w-[32px]"
              onClick={handleOpen}
            >
              <PlusIcon className="size-4" />
              <span className="hidden sm:inline ml-1">Create New</span>
            </Button>
          )}
          <button
            className={`border rounded-full size-8 text-gray-400 flex items-center justify-center shrink-0 ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center h-24">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : mealstableData.length > 0 ? (
          mealstableData.map((row: MealPlanDataType) => (
            <div
              key={row.id}
              className="bg-white border rounded-lg p-4 shadow-sm relative space-y-2"
              onClick={() => meal_plan !== "read" && handleEdit(row)}
            >
              <div className="flex justify-between items-start">
                <div className="flex gap-3 items-center">
                  {row.profile_img ? (
                    <img
                      src={
                        row.profile_img.includes('https://')
                          ? row.profile_img
                          : `${VITE_VIEW_S3_URL}/${row.profile_img}`
                      }
                      alt={row.meal_plan_name}
                      loading="lazy"
                      className="size-12 bg-gray-100 object-cover rounded-md"
                    />
                  ) : (
                    <div className="size-12 bg-gray-100 rounded-md"></div>
                  )}
                  <div>
                    <h3 className="font-bold text-base capitalize">{row.meal_plan_name}</h3>
                    <p className="text-xs text-gray-400">Visible for: {row.visible_for || "N/A"}</p>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DataTableRowActions
                    access={meal_plan}
                    handleEdit={handleEdit}
                    data={row}
                    refetch={refetch}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1 pt-2 border-t text-sm text-gray-500">
                <div>
                  <strong>Carbs:</strong> {row.carbs}%
                </div>
                <div>
                  <strong>Protein:</strong> {row.protein}%
                </div>
                <div>
                  <strong>Fats:</strong> {row.fats}%
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="h-24 text-center flex items-center justify-center border rounded-lg">
            No records found.
          </div>
        )}
      </div>

      <div className="hidden md:block rounded-none  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (meal_plan !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleEdit(row.original)
                      }
                    }}
                    className={meal_plan !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No meal plan found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {mealstableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <MealPlanForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        refetch={refetch}
        data={data}
      />
    </div>
  );
}
